import { useEffect, useState } from "react";
import { PropertyCard } from "@/components/public/PropertyCard";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchPublishedPropertiesWithoutPreflight } from "@/lib/public-properties-fallback";

interface PropertyLike {
  id: string;
  title: string;
  city: string | null;
  locality?: string | null;
  location?: string | null;
  state?: string | null;
  price: number | null;
  property_type: string;
  area_sqft?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  images?: string[] | null;
  description?: string | null;
  is_featured: boolean | null;
  slug?: string | null;
  rera_number?: string | null;
}

interface SimilarPropertiesProps {
  property: PropertyLike;
  limit?: number;
}

export default function SimilarProperties({ property, limit = 6 }: SimilarPropertiesProps) {
  const [items, setItems] = useState<PropertyLike[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const fetchSimilar = async () => {
      try {
        setLoading(true);

        // Fetch all published properties via XHR fallback
        const allProperties = await fetchPublishedPropertiesWithoutPreflight();
        const others = (allProperties as PropertyLike[]).filter(p => p.id !== property.id);

        // Score and sort by similarity
        const scored = others.map(p => {
          let score = 0;
          if (p.city && p.city === property.city) score += 3;
          if (p.property_type === property.property_type) score += 2;
          if (property.price && p.price) {
            const ratio = p.price / property.price;
            if (ratio >= 0.7 && ratio <= 1.3) score += 2;
          }
          if ((p as any).locality && (p as any).locality === (property as any).locality) score += 1;
          return { ...p, _score: score };
        });

        scored.sort((a, b) => b._score - a._score);
        const top = scored.slice(0, limit);

        if (isMounted) {
          setItems(top);
        }
      } catch (e) {
        console.warn("Failed to load similar properties", e);
        if (isMounted) setItems([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSimilar();
    return () => {
      isMounted = false;
    };
  }, [property.id, property.city, property.property_type, property.price, limit]);

  if (loading) {
    return (
      <section aria-labelledby="similar-properties" className="space-y-4">
        <h2 id="similar-properties" className="text-2xl font-semibold">
          Similar Properties in {property.city}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-72 w-full" />
          ))}
        </div>
      </section>
    );
  }

  if (!items || items.length === 0) return null;

  return (
    <section aria-labelledby="similar-properties" className="space-y-4">
      <h2 id="similar-properties" className="text-2xl font-semibold">
        Similar Properties in {property.city}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((p) => (
          <PropertyCard key={p.id} property={{
            ...p,
            location: (p as any).locality,
            city: p.city || '',
            is_featured: p.is_featured || false
          } as any} />
        ))}
      </div>
    </section>
  );
}
